import type { ShopHours } from './shopHoursUtils';

/**
 * Pure advisory helper for "technician hours vs shop hours".
 *
 * Surfacing rule (all advisory; never blocking):
 *   - Tech starts earlier than shop open  → startsBeforeOpen
 *   - Tech ends later than shop close      → endsAfterClose
 *   - Tech has hours on a day shop closed  → worksWhenShopClosed
 *
 * Quiet when shop hours are missing or unconfigured — we don't fire
 * speculative warnings while the operator is still building the shop
 * profile.
 *
 * The helper accepts BOTH editor shapes the app uses today:
 *   - Simple shape (lean wizard): just `{ shift_start, shift_end }`.
 *     With no per-day specification, we compare against the shop's
 *     earliest open across configured days and latest close across
 *     configured days. This is the most permissive comparison — we
 *     only warn when the tech's fixed shift falls fully outside the
 *     widest open band the shop has ever set.
 *   - Per-day shape (Shop Profile technician editor): `hours` is a map
 *     keyed by day name ("monday") OR day number ("1"), with values
 *     `{ open, close }` or null/undefined for days off. Each configured
 *     day is compared against the matching shop day.
 *
 * The two shapes can coexist on a single tech object — `hours` wins when
 * provided. The `shift_start`/`shift_end` legacy fields only drive the
 * comparison when no `hours` map is present.
 */

const DAY_NAMES = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;
type DayName = typeof DAY_NAMES[number];

export interface WeeklyHoursMap {
  [day: string]:
    | { open?: string | null; close?: string | null }
    | null
    | undefined;
}

export interface TechnicianHoursInput {
  shift_start?: string | null;
  shift_end?: string | null;
  /** Per-day map keyed by day name ("monday") or numeric ("1"). */
  hours?: WeeklyHoursMap | null;
}

export interface TechnicianHoursAdvisory {
  /** Display messages; already include the tech's name when supplied. */
  messages: string[];
  startsBeforeOpen: boolean;
  endsAfterClose: boolean;
  worksWhenShopClosed: boolean;
}

const EMPTY: TechnicianHoursAdvisory = Object.freeze({
  messages: [],
  startsBeforeOpen: false,
  endsAfterClose: false,
  worksWhenShopClosed: false,
});

function toMinutes(t: string | null | undefined): number | null {
  if (!t) return null;
  const parts = String(t).split(':');
  if (parts.length < 2) return null;
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
}

function dayNameFor(key: string): DayName | null {
  const lower = key.toLowerCase();
  if ((DAY_NAMES as readonly string[]).includes(lower)) return lower as DayName;
  const num = Number(key);
  if (Number.isInteger(num) && num >= 0 && num <= 6) return DAY_NAMES[num];
  return null;
}

function lookupShopDay(
  shopHours: ShopHours,
  day: DayName,
): { open?: string | null; close?: string | null } | null {
  const direct = shopHours[day];
  if (direct) return direct;
  const capital = day.charAt(0).toUpperCase() + day.slice(1);
  const cap = (shopHours as Record<string, unknown>)[capital];
  if (cap && typeof cap === 'object') {
    return cap as { open?: string | null; close?: string | null };
  }
  return null;
}

function shopHasAnyConfiguredDay(shopHours: ShopHours): boolean {
  for (const day of DAY_NAMES) {
    const d = lookupShopDay(shopHours, day);
    if (d?.open && d?.close) return true;
  }
  return false;
}

export function validateTechnicianHoursAgainstShopHours(
  tech: TechnicianHoursInput,
  shopHours: ShopHours | null | undefined,
  technicianName?: string,
): TechnicianHoursAdvisory {
  if (!shopHours || !shopHasAnyConfiguredDay(shopHours)) return EMPTY;

  const who = (technicianName ?? '').trim() || 'Technician';
  let startsBeforeOpen = false;
  let endsAfterClose = false;
  let worksWhenShopClosed = false;

  const hasPerDay =
    tech.hours &&
    typeof tech.hours === 'object' &&
    Object.keys(tech.hours).length > 0;

  if (hasPerDay) {
    for (const [key, val] of Object.entries(tech.hours!)) {
      const day = dayNameFor(key);
      if (!day) continue;
      const techOpen = toMinutes(val?.open ?? null);
      const techClose = toMinutes(val?.close ?? null);
      // Day-off / unconfigured technician day = nothing to compare.
      if (techOpen === null || techClose === null) continue;

      const shopDay = lookupShopDay(shopHours, day);
      const shopOpen = toMinutes(shopDay?.open ?? null);
      const shopClose = toMinutes(shopDay?.close ?? null);

      if (shopOpen === null || shopClose === null) {
        worksWhenShopClosed = true;
        continue;
      }
      if (techOpen < shopOpen) startsBeforeOpen = true;
      if (techClose > shopClose) endsAfterClose = true;
    }
  } else {
    const techStart = toMinutes(tech.shift_start ?? null);
    const techEnd = toMinutes(tech.shift_end ?? null);
    if (techStart === null && techEnd === null) return EMPTY;

    let earliestOpen: number | null = null;
    let latestClose: number | null = null;
    for (const day of DAY_NAMES) {
      const shopDay = lookupShopDay(shopHours, day);
      const o = toMinutes(shopDay?.open ?? null);
      const c = toMinutes(shopDay?.close ?? null);
      if (o !== null) earliestOpen = earliestOpen === null ? o : Math.min(earliestOpen, o);
      if (c !== null) latestClose = latestClose === null ? c : Math.max(latestClose, c);
    }
    if (earliestOpen === null || latestClose === null) return EMPTY;
    if (techStart !== null && techStart < earliestOpen) startsBeforeOpen = true;
    if (techEnd !== null && techEnd > latestClose) endsAfterClose = true;
  }

  const messages: string[] = [];
  if (startsBeforeOpen) messages.push(`${who} starts before shop opening time.`);
  if (endsAfterClose) messages.push(`${who} ends after shop closing time.`);
  if (worksWhenShopClosed) messages.push(`${who} has hours on a day the shop is closed.`);

  return { messages, startsBeforeOpen, endsAfterClose, worksWhenShopClosed };
}
